/**
 * Standalone-viewer store (replaces the full app store + reducers/index.js).
 *
 * The form-viewer components import this module directly as the redux
 * singleton (`import store from 'APP_ROOT/store'`) to read feature flags and
 * evaluate conditions, AND it is the same instance handed to <Provider>. So
 * there must be exactly one shared store, and it lives here.
 *
 * We combine only the slices the renderer touches — dropping the heavy app
 * reducers (administrator, organization, activity, persist, analytics, …) —
 * and seed `app`/`report`/`session` so fields render enabled:
 *   - app.internetAccess === true  AND  report.autoSaveDraft !== true
 *     → getFieldIsDisabled() keeps inputs interactive (otherwise every field
 *       renders disabled).
 */
import { compose, applyMiddleware, createStore, combineReducers } from 'redux';
import thunk from 'redux-thunk';
import { reducer as formReducer } from 'redux-form';
import { createBrowserHistory } from 'history';

import form from './reducers/form';
import session from './reducers/session';
import app from './reducers/app';
import report from './reducers/report';
import attachments from './reducers/attachments';
import modals from './reducers/modals';
import reviewForm from './reducers/review-form';

export const history = createBrowserHistory();

const rootReducer = combineReducers({
  app,
  session,
  form,
  report,
  attachments,
  modals,
  reviewForm,
  forms: formReducer,
});

// Identity of the previewing "user" — no real agency/account is needed, but the
// shape must satisfy the session selectors (currentUser.{id,userId,agencyId,agency}).
const VIEWER_USER = {
  id: 'standalone-viewer',
  userId: 'standalone-viewer',
  agencyId: 1,
  agency: {
    id: 1,
    name: 'Standalone Viewer',
    timezone: 'America/Chicago',
  },
  featureFlags: [],
};

const preloadedState = {
  app: {
    internetAccess: true,
    blockNavigation: false,
    sidebarCollapsed: false,
  },
  report: {
    autoSaveDraft: false,
    loading: false,
  },
  session: {
    currentUser: VIEWER_USER,
  },
};

const composeEnhancers =
  typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    : compose;

const store = createStore(
  rootReducer,
  preloadedState,
  composeEnhancers(applyMiddleware(thunk))
);

export default store;
